import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * 让 canonical 与 og:url 跟着路由走。
 *
 * 站点同时可从 bestsmy.com 与 sanmiyun.com 访问，两个域名内容完全相同。
 * 搜索引擎会把它们视为两套重复内容并分散权重，因此需要把 canonical
 * 统一指向主域名 bestsmy.com —— 该域名也是 App Store 上架信息与
 * 各产品法务页链接使用的域名，保持一致可以避免审核方看到跨域跳转。
 *
 * 这是单页应用，index.html 里的静态标签只在首次加载时正确；
 * 路由切换后必须同步更新，否则所有内页的 canonical 都指向首页。
 *
 * 没有引入 react-helmet：只为维护三个标签值再加一个依赖不划算，
 * 直接操作这几个节点的行为是确定的。
 */

const PRIMARY_ORIGIN = 'https://bestsmy.com';

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function upsertMeta(property: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.content = content;
}

export function useCanonical() {
  const { pathname } = useLocation();

  useEffect(() => {
    // 查询串与哈希不进 canonical：/news?cat=company 与 /news 是同一篇内容，
    // 分开收录只会让筛选参数产生一堆近重复页
    const url = `${PRIMARY_ORIGIN}${pathname === '/' ? '/' : pathname.replace(/\/+$/, '')}`;
    upsertLink('canonical', url);
    upsertMeta('og:url', url);
  }, [pathname]);
}

export default useCanonical;
